import axios from "axios";

// Change log API (FrothTestOps-Revamp-Website-Backend, app/api/routes/changelog.py).
// Uses its own axios instance so Self-Service tokens are never attached to these calls.
export const BASE_URL = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

const client = axios.create({ baseURL: BASE_URL, timeout: 20000 });

const SESSION_KEY = "frothChangelogAdmin";

export const absoluteUrl = path => (!path ? null : /^https?:\/\//i.test(path) ? path : `${BASE_URL}${path}`);

export const fetchChangelog = ({ timeout = 8000 } = {}) => client.get("/api/changelog", { timeout }).then(r => r.data);

// ---- admin session (sessionStorage: cleared when the tab closes) ----

export function getAdminSession() {
  try {
    const session = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (!session || !session.token || !session.expiresAt) return null;
    if (session.expiresAt * 1000 <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export function clearAdminSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // storage unavailable; nothing to clear
  }
}

const authHeaders = () => {
  const session = getAdminSession();
  return session ? { Authorization: `Bearer ${session.token}` } : {};
};

export async function adminLogin(username, password) {
  const { data } = await client.post("/api/changelog/admin/login", { username, password });
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  } catch {
    // Private mode without storage: the session lasts until reload.
  }
  return data;
}

export const adminCheckSession = () =>
  client.get("/api/changelog/admin/session", { headers: authHeaders() }).then(r => r.data);

export const adminSaveChangelog = doc =>
  client.put("/api/changelog/admin/changelog", doc, { headers: authHeaders() }).then(r => r.data);

export const adminUploadImage = file =>
  client
    .post("/api/changelog/admin/images", file, {
      params: { filename: file.name },
      headers: { ...authHeaders(), "Content-Type": file.type || "application/octet-stream" },
      timeout: 60000,
    })
    .then(r => r.data);

export const adminListBackups = () =>
  client.get("/api/changelog/admin/backups", { headers: authHeaders() }).then(r => r.data.backups);

export const adminRestoreBackup = (name, revision) =>
  client
    .post(`/api/changelog/admin/backups/${encodeURIComponent(name)}/restore`, { revision }, { headers: authHeaders() })
    .then(r => r.data);

// ---- errors ----

export const isUnauthorized = err => err?.response?.status === 401;
export const isConflict = err => err?.response?.status === 409;

/** Readable message(s) from an API error. Returns a list of strings. */
export function errorMessages(err) {
  if (!err?.response) {
    return [err?.code === "ECONNABORTED" ? "The server took too long to respond. Please try again." : "Cannot reach the server. Check your connection and try again."];
  }
  const detail = err.response.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => (d.path ? `${d.path}: ${d.message}` : d.message || d.msg || String(d)));
  }
  if (detail && typeof detail === "object") return [detail.message || "Request failed"];
  if (typeof detail === "string") return [detail];
  return [`Request failed (${err.response.status})`];
}
